import { Alert, AmountField, Dialog, MenuItem, SelectTextField, Stack, TextField } from "@tm/components"
import { useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import { useForm, Controller, FormProvider } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { errorHandling } from "@tm/utils"
import { CostEstimationOptions, SelectedPriceTypeMode } from "@tm/models"
import { DialogCustomArticleAdd } from "@bundles/compilations/components/compilations/OwnArticles/ArticleEditDialog"
import { EditDialogHeader } from "../../../../../_shared/PartDialog/EditDialogHeader"
import { BasketPart, BasicBasketPartInfo } from "../../../../../../models"
import { PartPriceFields } from "./PartPriceFields"
import { useArticleFormDefaults } from "../../../../../../hooks/Forms/useArticleFormDefaults"
import { CustomerDefaults } from "../../../../../../data/model"
import { OwnArticleFields } from "./OwnArticleFields"
import { useCustomPartSchema } from "./hooks/useCustomPartSchema"
import { useSaveOwnArticle } from "./hooks/useSaveOwnArticle"
import { useMapOwnArticleRequest } from "./hooks/useMapOwnArticleRequest"

export type DialogCustomPartAdd = { type: "add" }
export type DialogCustomPartEdit = { type: "edit"; part: BasketPart }
export type DialogCustomPart = DialogCustomPartAdd | DialogCustomPartEdit

export type OwnArticleRequest = Omit<DialogCustomArticleAdd, "type">

type Props = {
    customPartInfo: DialogCustomPart
    currencyCode: string
    costEstimationOptions?: CostEstimationOptions
    customerDefaults?: CustomerDefaults
    disableHeaderButtons?: boolean
    onCloseButtonClick(): void
    onEditPart?(part: BasketPart, editBasketPartInfo: BasicBasketPartInfo): void
    onAddCustomPart?(basicBasketPartInfo: BasicBasketPartInfo, quantityValue: number): void
}

export function CustomPartEditDialog(props: Props) {
    const {
        customPartInfo,
        onEditPart,
        currencyCode,
        disableHeaderButtons,
        onCloseButtonClick,
        onAddCustomPart,
        customerDefaults,
        costEstimationOptions,
    } = props
    const { translateText } = useLocalization()
    const { vatRateItems } = useArticleFormDefaults()
    const [saveError, setSaveError] = useState<string | undefined>()
    const saveOwnArticle = useSaveOwnArticle()
    const mapOwnArticleRequest = useMapOwnArticleRequest(vatRateItems)

    const defaultValues = useMemo(() => {
        if (customPartInfo.type === "add") {
            return {
                saveAsOwnArticle: false,
                partNumber: "",
                description: "",
                regularPrice: 0,
                surcharge: customerDefaults?.partsSurcharge ?? 0,
                regularPriceWithSurcharge: 0,
                rebate: customerDefaults?.partsRebate ?? 0,
                offerPrice: 0,
                vatType: vatRateItems?.first()?.vatRate.vatType ?? 0,
                quantity: 1,
            }
        }

        const { calculatedPart, partItem } = customPartInfo.part || {}
        const { articleInformation, costEstimationItem } = partItem || {}
        return {
            partNumber: articleInformation?.articleNumber ?? "",
            description: articleInformation?.description ?? "",
            rebate: calculatedPart?.rebate ?? 0,
            surcharge: calculatedPart?.surcharge ?? 0,
            regularPrice: calculatedPart?.regularPrice?.value ?? 0,
            regularPriceWithSurcharge: calculatedPart?.regularPriceWithSurcharge?.value ?? 0,
            offerPrice: calculatedPart?.offerPrice?.value ?? 0,
            vatType: costEstimationItem?.vatRates?.find((vatRate) => vatRate.isSelected)?.vatType ?? 0,
            quantity: 1,
        }
    }, [])

    const schema = useCustomPartSchema()
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const { handleSubmit, control, watch } = form
    const saveAsOwnArticle = watch("saveAsOwnArticle")

    async function handleConfirmButtonClick(data: z.infer<typeof schema>) {
        const { vatType, quantity, regularPrice, ...rest } = data
        const vatRate = vatRateItems.find((vr) => vr.vatRate.vatType === vatType)?.vatRate
        let success: boolean = false

        if (!vatRate) {
            return
        }
        if (customPartInfo.type === "edit") {
            onEditPart?.(customPartInfo.part, {
                ...rest,
                regularPrice,
                vatRate: {
                    vatRate: vatRate.vatValue,
                    vatType: vatRate.vatType,
                },
            })
        } else {
            onAddCustomPart?.(
                {
                    ...rest,
                    retailPrice: !costEstimationOptions?.priceTypeMode ? regularPrice : undefined,
                    purchasePrice: costEstimationOptions?.priceTypeMode === SelectedPriceTypeMode.Purchase ? regularPrice : undefined,
                    vatRate: {
                        vatRate: vatRate.vatValue,
                        vatType: vatRate.vatType,
                    },
                },
                quantity ?? 1
            )

            if (saveAsOwnArticle) {
                const request = mapOwnArticleRequest(data)
                success = await saveOwnArticle({ ...request }, data.note)
                setSaveError(success ? undefined : translateText(13452))
            }
        }

        if (success || !saveAsOwnArticle) {
            onCloseButtonClick()
        }
    }

    return (
        <Dialog open position="middle" fullWidth maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <Stack gap={2}>
                        <EditDialogHeader
                            isSaving={disableHeaderButtons}
                            onCloseButtonClick={onCloseButtonClick}
                            customPartTitleText={translateText(customPartInfo.type === "edit" ? 757 : 13906)}
                        />
                        {saveError && <Alert size="small" severity="error" title={saveError} variant="outlined" sx={{ mb: 2 }} />}
                        <Stack direction="row" gap={1} flex={1}>
                            <Controller
                                control={control}
                                name="partNumber"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        label={translateText(87)}
                                        size="extralarge"
                                        inputProps={{ maxLength: 50 }}
                                        sx={{ width: "235px" }}
                                        {...field}
                                        {...errorHandling(fieldState)}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="description"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        required
                                        label={translateText(25)}
                                        size="extralarge"
                                        fullWidth
                                        inputProps={{ maxLength: 200 }}
                                        {...field}
                                        {...errorHandling(fieldState)}
                                    />
                                )}
                            />
                        </Stack>
                        <Stack direction="row" flex={1} gap={2} alignItems="center">
                            <Controller
                                control={control}
                                name="quantity"
                                render={({ field, fieldState }) => (
                                    <AmountField
                                        label={translateText(89)}
                                        required
                                        disabled={customPartInfo.type === "edit"}
                                        min={0}
                                        max={9999}
                                        step={0.1}
                                        size="extralarge"
                                        sx={{ width: "100px" }}
                                        {...field}
                                        {...errorHandling(fieldState)}
                                    />
                                )}
                            />
                            <PartPriceFields currencyCode={currencyCode} />
                            <Controller
                                control={control}
                                name="vatType"
                                render={({ field, fieldState }) => (
                                    <SelectTextField
                                        label={translateText(706)}
                                        size="extralarge"
                                        sx={{ width: "300px" }}
                                        {...field}
                                        {...errorHandling(fieldState)}
                                    >
                                        {vatRateItems.map(({ vatRate, text }) => (
                                            <MenuItem key={vatRate.vatType} value={vatRate.vatType}>
                                                {text}
                                            </MenuItem>
                                        ))}
                                    </SelectTextField>
                                )}
                            />
                        </Stack>
                        {customPartInfo.type === "add" && <OwnArticleFields onSaveError={setSaveError} />}
                    </Stack>
                </form>
            </FormProvider>
        </Dialog>
    )
}
